import { useCallback, useEffect, useRef, useState } from "react";
import { CallStatus } from "@aihumanity/voice-sdk/react";

import {
  aggregateObservations,
  observationFromScores,
} from "./affectFusion";
import { ensureDemoSession } from "./demoSession";

const FACE_MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/latest/blaze_face_short_range.tflite";
const VISION_BUNDLE_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/vision_bundle.mjs";
const VISION_WASM_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";
const INPUT_SIZE = 224;
const ROI_PAD = 0.3;
const SAMPLE_MS = 1000;

function cameraErrorMessage(error) {
  if (error.name === "NotAllowedError" || error.name === "SecurityError") {
    return "Camera permission denied; continuing voice-only.";
  }
  if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
    return "No camera found; continuing voice-only.";
  }
  if (error.name === "NotReadableError" || error.name === "TrackStartError") {
    return "Camera unavailable; continuing voice-only.";
  }
  return `${error.name || "CameraError"}: ${error.message || error}`;
}

function paddedRoi(x, y, width, height, imageWidth, imageHeight) {
  const cx = x + width / 2;
  const cy = y + height / 2;
  const size = Math.max(width, height) * (1 + ROI_PAD);
  const roi = {
    x: Math.max(0, Math.round(cx - size / 2)),
    y: Math.max(0, Math.round(cy - size / 2)),
    width: Math.round(Math.min(size, imageWidth)),
    height: Math.round(Math.min(size, imageHeight)),
  };
  if (roi.x + roi.width > imageWidth) roi.x = imageWidth - roi.width;
  if (roi.y + roi.height > imageHeight) roi.y = imageHeight - roi.height;
  return roi;
}

function mapFaceScores(scores) {
  return {
    anger: scores?.Anger,
    disgust: scores?.Disgust,
    fear: scores?.Fear,
    happy: scores?.Happiness,
    neutral: scores?.Neutral,
    sad: scores?.Sadness,
    surprise: scores?.Surprise,
  };
}

export default function useFaceEmotionSampler({
  enabled,
  isLive,
  status,
  onTimeline,
}) {
  const videoRef = useRef(null);
  const cropRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const detectorPromiseRef = useRef(null);
  const samplesRef = useRef([]);
  const runningRef = useRef(false);
  const faceGateStatusRef = useRef("idle");
  const previousStatusRef = useRef(status);
  const [cameraStatus, setCameraStatus] = useState("off");
  const [faceObservation, setFaceObservation] = useState(null);
  const [faceScores, setFaceScores] = useState(null);
  const [faceError, setFaceError] = useState(null);
  const [sampleCount, setSampleCount] = useState(0);

  const addTimeline = useCallback((message) => {
    onTimeline?.({ type: "face", message, at: Date.now() });
  }, [onTimeline]);

  const stopCamera = useCallback(() => {
    runningRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    samplesRef.current = [];
    faceGateStatusRef.current = "idle";
    setFaceObservation(null);
    setFaceScores(null);
    setFaceError(null);
    setSampleCount(0);
    setCameraStatus("off");
  }, []);

  const loadDetector = useCallback(async () => {
    if (detectorRef.current || detectorPromiseRef.current) {
      return detectorPromiseRef.current;
    }

    detectorPromiseRef.current = (async () => {
      setCameraStatus("loading-detector");
      const { FaceDetector, FilesetResolver } = await import(
        /* webpackIgnore: true */ VISION_BUNDLE_URL
      );
      const vision = await FilesetResolver.forVisionTasks(VISION_WASM_URL);
      detectorRef.current = await FaceDetector.createFromOptions(vision, {
        baseOptions: { modelAssetPath: FACE_MODEL_URL },
        runningMode: "VIDEO",
        minDetectionConfidence: 0.55,
      });
      addTimeline("face detector loaded");
      return true;
    })().catch((error) => {
      detectorPromiseRef.current = null;
      setFaceError(`Face detector unavailable: ${error.message}`);
      setCameraStatus("unavailable");
      addTimeline(`face detector unavailable: ${error.message}`);
      return false;
    });

    return detectorPromiseRef.current;
  }, [addTimeline]);

  const startCamera = useCallback(async () => {
    if (streamRef.current) return true;
    if (!navigator.mediaDevices?.getUserMedia) {
      setFaceError("Camera API unavailable; continuing voice-only.");
      setCameraStatus("unavailable");
      return false;
    }

    setFaceError(null);
    setCameraStatus("opening");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 960 },
          height: { ideal: 540 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      addTimeline("camera ready");
      const detectorReady = await loadDetector();
      if (!detectorReady || !detectorRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
        runningRef.current = false;
        return false;
      }
      runningRef.current = true;
      setCameraStatus("ready");
      return true;
    } catch (error) {
      const message = cameraErrorMessage(error);
      setFaceError(message);
      setCameraStatus("unavailable");
      addTimeline(message);
      return false;
    }
  }, [addTimeline, loadDetector]);

  const detectRoi = useCallback(() => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight) return null;
    if (!detectorRef.current) return null;

    const result = detectorRef.current.detectForVideo(video, performance.now());
    const box = result.detections?.[0]?.boundingBox;
    if (!box) return null;
    return paddedRoi(
      box.originX,
      box.originY,
      box.width,
      box.height,
      video.videoWidth,
      video.videoHeight,
    );
  }, []);

  const drawCrop = useCallback((roi) => {
    const video = videoRef.current;
    const canvas = cropRef.current;
    const ctx = canvas?.getContext("2d", { willReadFrequently: true });
    if (!video || !canvas || !ctx) return null;

    ctx.save();
    ctx.clearRect(0, 0, INPUT_SIZE, INPUT_SIZE);
    ctx.translate(INPUT_SIZE, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(
      video,
      roi.x,
      roi.y,
      roi.width,
      roi.height,
      0,
      0,
      INPUT_SIZE,
      INPUT_SIZE,
    );
    ctx.restore();
    return canvas.toDataURL("image/jpeg", 0.84);
  }, []);

  const sampleFace = useCallback(async () => {
    const roi = detectRoi();
    if (!roi) {
      samplesRef.current = [];
      setFaceObservation(null);
      setFaceScores(null);
      setCameraStatus("no-face");
      if (faceGateStatusRef.current !== "no-face") {
        addTimeline("FER sample skipped: no detected face");
      }
      faceGateStatusRef.current = "no-face";
      return;
    }
    if (faceGateStatusRef.current === "no-face") {
      addTimeline("FER face lock restored");
    }
    faceGateStatusRef.current = "face";
    const image = drawCrop(roi);
    if (!image) return;

    const started = performance.now();
    await ensureDemoSession();
    const response = await fetch("/.netlify/functions/predict-face", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ image, modelId: "exp15_best" }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || `Face request failed: HTTP ${response.status}`);
    }

    const observedAt = Date.now();
    const observation = observationFromScores("face", mapFaceScores(payload.scores || {}), {
      confidence: typeof payload.confidence === "number" ? payload.confidence : undefined,
      quality: 1,
      observedAt,
      detail: `${roi.width}x${roi.height}`,
    });
    samplesRef.current = [...samplesRef.current.slice(-4), observation];
    const aggregate = aggregateObservations(samplesRef.current);
    setFaceObservation(aggregate);
    setFaceScores(aggregate?.scores || null);
    setSampleCount((count) => count + 1);
    setCameraStatus("sampling");
    addTimeline(`FER ${observation.label} ${Math.round(observation.confidence * 100)}% in ${Math.round(performance.now() - started)}ms`);
  }, [addTimeline, detectRoi, drawCrop]);

  useEffect(() => {
    if (!enabled || !isLive) {
      stopCamera();
      return undefined;
    }
    void startCamera();
    return undefined;
  }, [enabled, isLive, startCamera, stopCamera]);

  useEffect(() => {
    if (!enabled || !isLive || !streamRef.current) return undefined;
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      if (cancelled || !runningRef.current) return;
      const shouldSample = status === CallStatus.LISTENING;
      if (shouldSample) {
        try {
          await sampleFace();
          setFaceError(null);
        } catch (error) {
          setFaceError(error.message);
          setCameraStatus("error");
          addTimeline(error.message);
        }
      }
      if (!cancelled) timer = setTimeout(tick, SAMPLE_MS);
    };

    timer = setTimeout(tick, 250);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [addTimeline, cameraStatus, enabled, isLive, sampleFace, status]);

  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = status;
    if (status !== CallStatus.LISTENING || previousStatus === CallStatus.LISTENING) {
      return;
    }
    samplesRef.current = [];
    setFaceObservation(null);
    setFaceScores(null);
    setSampleCount(0);
  }, [status]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  return {
    cameraStatus,
    faceObservation,
    faceScores,
    faceError,
    sampleCount,
    startCamera,
    stopCamera,
    videoRef,
    cropRef,
  };
}
